import api from '@/components/mixins/baseApi';
import methodsExhibitors from '@/components/mixins/methodsExhibitors';
import {
  readPair,
  writePair,
  readDirectory,
  writeDirectory,
} from '@/views/pages/MapView/navigation/pairCache';

/**
 * Граф приходит строкой — так его отдаёт сервер. Разбираем здесь, а не
 * дальше по дороге: не разобрался — значит графа нет, и модуль работает
 * как обычная карта. Молчать об этом нельзя, иначе испорченная выгрузка
 * будет неотличима от мероприятия без навигации.
 */
function readGraph(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;

  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn('[карта] граф не разобрался:', error.message);
    return null;
  }
}

export default {
  mixins: [api, methodsExhibitors],
  methods: {
    getMap() {
      this.$store.commit('setProgressVal', 1);
      return this.baseAxios({
        method: 'get',
        url: `${this.$store.getters.apiBasePath}/map`,
        onDownloadProgress: (progressEvent) => {
          this.$store.commit('setProgressVal', Math.round(progressEvent.progress * 100));
        },
      })
        .then((data) => data.results || null)
        .catch(() => {
          this.$store.commit('setProgressVal', 100);
          return false;
        });
    },

    /**
     * Карты вместе с графом навигации.
     *
     * Граф приходит в том же ответе, что и карта: порознь они
     * бессмысленны, а два запроса дают половину обновления — новую
     * карту со старым графом. Пока бекенд полей не отдаёт, пара
     * подставляется синтетикой в dev; в production подставлять нечем,
     * и модуль работает как обычная карта.
     *
     * Загрузилось — кладём в кэш и отдаём; не загрузилось — отдаём
     * прежнее из кэша: в павильоне связи может не быть вовсе.
     *
     * @returns {Promise<Array|null|false>} то же, что getMap, но у
     *   каждой карты есть `nav_graph` и `map_name` — хотя бы null.
     */
    async getMapPair() {
      const exhId = this.$store.getters.getExhId;
      const maps = await this.getMap();

      if (Array.isArray(maps) && maps.length) {
        const fresh = maps.map((item) => ({
          ...item,
          nav_graph: readGraph(item.nav_graph),
          map_name: item.map_name || null,
        }));
        writePair(exhId, fresh);
        return fresh;
      }

      return readPair(exhId) || maps;
    },

    /**
     * Справочник компаний: название и номер стенда.
     *
     * Собирается обходом всех страниц и дальше живёт у страницы карты:
     * сервер параметр `search` к /exhibitor/page не применяет, поэтому
     * искать всё равно приходится у себя — а собранный справочник это
     * же даёт и без сети.
     *
     * @returns {Promise<Array>} пустой массив, если справочника нет —
     *   это не ошибка, поиск по номеру стенда работает и без него.
     */
    async getStandDirectory() {
      const exhId = this.$store.getters.getExhId;
      const collected = await this.walkDirectoryPages();

      if (collected.length) {
        writeDirectory(exhId, collected);
        return collected;
      }

      return readDirectory(exhId) || [];
    },

    /** Обход страниц справочника. Пусто — значит не дошли до сервера. */
    async walkDirectoryPages() {
      const first = await this.getExhibitorList(1);
      if (!first || !Array.isArray(first.results)) return [];

      const pages = first.num_pages || 1;
      if (pages <= 1) return first.results;

      // Страницы со второй тянем разом: их число известно из первой
      const rest = await Promise.all(
        Array.from({ length: pages - 1 }, (unused, index) => this.getExhibitorList(index + 2)),
      );

      return rest.reduce(
        (all, chunk) => (chunk && Array.isArray(chunk.results) ? all.concat(chunk.results) : all),
        first.results.slice(),
      );
    },
  },
};
